<template>
  <div>
    <h1>{{ headline }}</h1>
    <h2>{{ subline }}</h2>
    <div id="sunburst">
      <div
        v-if="currentSelected.parent"
        :class="{ hovering: hoveringBackbutton }"
        id="backButton"
      >
        <img id="arrow" src="@/assets/arrow.svg" />Back
      </div>
      <div
        v-if="tooltip.show"
        id="tooltip"
        :style="{
          top: tooltip.position.top + 'px',
          left: tooltip.position.left + 'px',
        }"
      >
        <span class="bold">{{ tooltip.name }}</span
        ><br />
        {{ formatCurrency(tooltip.value) }}<br />
      </div>
    </div>
    <div>
      <h3>Source: European Commission 2021</h3>
    </div>
  </div>
</template>

<script>
import budgetData from "../data/Horizon-Europe-Budget.json";
import * as d3 from "d3";
import color from "color";
import smartTruncate from "smart-truncate";

export default {
  name: "Horizon-Europe-Viz",
  data() {
    return {
      headline: "HORIZON EUROPE BUDGET",
      subline: "(in billion euro)",
      currentSelected: {},
      hoveringBackbutton: false,
      tooltip: {
        show: true,
        name: "",
        value: null,
        percent: null,
        position: {
          left: 0,
          top: 0,
        },
      },
    };
  },
  mounted() {
    this.initDataVis();
  },
  methods: {
    formatCurrency(currentValue) {
      const locale = d3.formatLocale({
        decimal: ".",
        thousands: ".",
        grouping: [3],
        currency: ["", "\u00a0b €"],
        minus: "\u2212",
        percent: "\u202f%",
      });

      return locale.format("$,")(currentValue);
    },
    async initDataVis() {
      const that = this;
      const width = 795;
      const radius = width / 6.3;

      const partition = () => {
        const root = d3
          .hierarchy(budgetData)
          .sum((d) => d.value)
          .sort((a, b) => b.value - a.value);
        return d3.partition().size([2 * Math.PI, root.height + 1])(root);
      };

      function segmentColor(_topData, datum) {
        const index = datum.parent.children.findIndex(
          (item) => item.data.name === datum.data.name
        );
        const colorMix =
          datum.parent.children.length > 1
            ? index / (datum.parent.children.length - 1)
            : 0;

        if (datum.depth == 1) datum.data.resultingColor = datum.data.basicColor;
        else if (datum.depth == 2) {
          datum.data.resultingColor = color(datum.parent.data.childrenColorA)
            .mix(color(datum.parent.data.childrenColorB), colorMix)
            .hex();
        } else if (datum.depth == 3) {
          const strength = 0.4;
          datum.data.resultingColor = color(datum.parent.data.resultingColor)
            .darken((1 - colorMix) * strength)
            .hex();
        }

        return datum.data.resultingColor;
      }

      const arc = d3
        .arc()
        .startAngle((d) => d.x0)
        .endAngle((d) => d.x1)
        .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(radius * 1.5)
        .innerRadius((d) => d.y0 * radius)
        .outerRadius((d) => Math.max(d.y0 * radius, d.y1 * radius - 1));

      function format() {
        return d3.format(",d");
      }

      const root = partition();

      root.each((d) => (d.current = d));

      // Font-Style
      const svg = d3
        .select("#sunburst")
        .append("svg")
        .attr("viewBox", [0, 0, width, width])
        .style("font", "0.8rem sans-serif");

      const g = svg
        .append("g")
        .attr("transform", `translate(${width / 2},${width / 2})`);

      const segmentMouseover = function(event, d) {
        that.tooltip.show = true;

        d3.select(this).attr(
          "fill",
          color(d.data.resultingColor)
            .lighten(0.2)
            .hex()
        );
      };
      const segmentMousemove = function(event, d) {
        that.tooltip.name = d.data.name;
        that.tooltip.value = d.value;
        that.tooltip.percent = (d.value * 100) / root.value;

        const boundingClientRect = document
          .getElementById("sunburst")
          .getBoundingClientRect();

        that.tooltip.position.left = event.pageX - boundingClientRect.left;
        that.tooltip.position.top = event.pageY - boundingClientRect.top;
      };
      const segmentMouseleave = function(event, d) {
        that.tooltip.show = false;
        d3.select(this).attr("fill", d.data.resultingColor);
      };

      const path = g
        .append("g")
        .selectAll("path")
        .data(root.descendants().slice(1))
        .join("path")
        .attr("fill", (_d) => {
          let topParent;
          let tempD = _d;
          while (tempD.depth > 1) tempD = tempD.parent;
          topParent = tempD;
          return segmentColor(topParent.data, _d);
        })
        .attr("fill-opacity", (d) =>
          arcVisible(d.current) ? (d.children ? 1 : 1) : 0
        )
        .attr("d", (d) => arc(d.current));

      path
        .filter((d) => d.children)
        .style("cursor", "pointer")
        .on("click", clicked);

      path
        .on("mouseover", segmentMouseover)
        .on("mousemove", segmentMousemove)
        .on("mouseleave", segmentMouseleave);

      that.currentSelected = root;

      const label = g
        .append("g")
        .attr("pointer-events", "none")
        .attr("text-anchor", "middle")
        .style("user-select", "none")
        .selectAll("text")
        .data(root.descendants().slice(1))
        .join("text")
        .attr("dy", "0.35em")
        .attr("fill", "white")
        .attr("fill-opacity", (d) => +labelVisible(d.current))
        .attr("transform", (d) => labelTransform(d.current))
        .text((d) => smartTruncate(d.data.name, 16, { position: 16 }));

      const parent = g
        .append("circle")
        .datum(root)
        .attr("r", radius)
        .attr("fill", "none")
        .attr("pointer-events", "all")
        .style("cursor", "pointer")
        .on("click", clicked)
        .on("mouseover", () => {
          that.hoveringBackbutton = true;
        })
        .on("mouseleave", () => {
          that.hoveringBackbutton = false;
        });

      function clicked(event, p) {
        that.currentSelected = p;
        let breadcrumb = `${p.data.name}`;
        if (p.parent) breadcrumb = `${p.parent.data.name} >> ${breadcrumb}`;
        if (p.parent?.parent)
          breadcrumb = `${p.parent.parent.data.name} >> ${breadcrumb}`;

        // console.log(breadcrumb);

        that.headline = breadcrumb;

        parent.datum(p.parent || root);

        root.each(
          (d) =>
            (d.target = {
              x0:
                Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) *
                2 *
                Math.PI,
              x1:
                Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) *
                2 *
                Math.PI,
              y0: Math.max(0, d.y0 - p.depth),
              y1: Math.max(0, d.y1 - p.depth),
            })
        );

        const t = g.transition().duration(750);

        // Transition the data on all arcs, even the ones that aren’t visible,
        // so that if this transition is interrupted, entering arcs will start
        // the next transition from the desired position.
        path
          .transition(t)
          .tween("data", (d) => {
            const i = d3.interpolate(d.current, d.target);
            return (t) => (d.current = i(t));
          })
          .filter(function(d) {
            return +this.getAttribute("fill-opacity") || arcVisible(d.target);
          })
          .attr("fill-opacity", (d) =>
            arcVisible(d.target) ? (d.children ? 1 : 1) : 0
          )
          .attrTween("d", (d) => () => arc(d.current));

        label
          .filter(function(d) {
            return +this.getAttribute("fill-opacity") || labelVisible(d.target);
          })
          .transition(t)
          .attr("fill-opacity", (d) => +labelVisible(d.target))
          .attrTween("transform", (d) => () => labelTransform(d.current));
      }

      function arcVisible(d) {
        return d.y1 <= 3 && d.y0 >= 1 && d.x1 > d.x0;
      }

      function labelVisible(d) {
        return d.y1 <= 3 && d.y0 >= 1 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
      }

      function labelTransform(d) {
        const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
        const y = ((d.y0 + d.y1) / 2) * radius;
        return `rotate(${x - 90}) translate(${y},0) rotate(${
          x < 180 ? 0 : 180
        })`;
      }

      // return svg.node();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1 {
  font-size: 1em;
  margin-top: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  margin-top: 40px;
  max-width: 85%;
}

@media (min-width: 568px) {
  h1 {
    font-size: 1.5em;
  }
}

h2 {
  font-size: 0.85em;
  text-align: center;
  margin-bottom: 2em;
}

@media (min-width: 568px) {
  h2 {
    font-size: 1em;
  }
}

h3 {
  font-size: 0.85em;
  text-align: center;
}

@media (min-width: 568px) {
  h3 {
    font-size: 1em;
  }
}

#sunburst {
  position: relative;
  max-width: 800px;
}

#sunburst #tooltip {
  position: absolute;
  background-color: #01374f;
  color: white;
  padding: 5px;
  transform: translate(-50%, 20px);
  width: 100px;
  font-size: 0.6rem;
}

@media (min-width: 568px) {
  #sunburst #tooltip {
    padding: 5px;
    width: 150px;
    font-size: 0.8rem;
  }
}

#backButton {
  position: absolute;
  top: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 50%;
  transform: translate(50%, -50%);
  /* cursor: pointer; */
  font-weight: bold;
  display: flex;
  align-items: center;
  pointer-events: none;
  font-size: 0.8em;
  color: #01374f;
}

@media (min-width: 568px) {
  #backButton {
    font-size: 1.5em;
  }
}

#backButton.hovering {
  text-decoration: underline;
  text-decoration-color: #01374f;
}

#arrow {
  height: 15px;
  margin-right: 7px;
}

@media (min-width: 568px) {
  #arrow {
    height: 25px;
    margin-right: 7px;
  }
}

.bold {
  font-weight: bold;
}
</style>
